import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Camera, Sparkles, ShieldCheck, Wand2, FileText, Pencil } from 'lucide-react';
import { getAllCategories, getAllProducts, normalize, getCategory, getSubcategories } from '../../data/catalog';
import { addCustomCategory, addCustomProduct, addSubcategory, addVariantToFabric, getCustomSubcategories, slugify, updateCustomProduct } from '../../utils/customProducts';
import { CoverAIGenerator } from '../../components/admin/CoverAIGenerator';
import { fileToDataUrl } from '../../utils/siteConfig';
import { uploadImage, PHOTO_STATUS } from '../../utils/photoStatus';
import { useQueue } from '../../context/QueueContext';

const titleCase = (s) =>
  (s || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w === w.toUpperCase() && w.length > 2 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');

const KNOWN_NAMES = {
  'micro fibra': 'Microfibra',
  microfibra: 'Microfibra',
  'la fria': 'Lã Fria',
  'azul marinho': 'Azul-marinho',
  'verde musgo': 'Verde-musgo',
};

const normalizeName = (s) => KNOWN_NAMES[normalize(s)] || titleCase(s.trim());

const CATEGORY_HINTS = [
  ['linhos', ['linho']],
  ['malhas', ['malha', 'ribana', 'moletinho', 'piquet', 'canelada']],
  ['estampados', ['tricoline', 'estampad', 'floral', 'poa', 'vichy', 'folhagen']],
  ['festa', ['veludo', 'crepe', 'cetim', 'tule', 'festa', 'seda']],
  ['decoracao', ['decoracao', 'cortina', 'jacquard', 'cotele', 'impermeavel']],
  ['alfaiataria', ['alfaiataria', 'la fria', 'oxford', 'sarja', 'tropical', 'terno', 'microfibra']],
];

// Largura escrita em texto livre: "Algodão Cru 2,65 m", "largura 2,65", "largura: 2,65 m".
// A largura pertence ao campo de dados técnicos — nunca vira variante nem parte do nome.
function extractWidth(rawText) {
  const m =
    rawText.match(/largura\s*:?\s*(\d{1,3}[.,]\d{1,2})\s*(?:m(?:etros?)?)?/i) ||
    rawText.match(/(\d{1,3}[.,]\d{1,2})\s*m(?:etros?)?\b/i);
  if (!m) return null;
  return {
    width: `${m[1].replace('.', ',')} m`,
    cleanText: `${rawText.slice(0, m.index)} ${rawText.slice(m.index + m[0].length)}`.replace(/\s+/g, ' ').trim(),
  };
}

// Produto novo: o nome digitado é preservado INTEIRO — a IA nunca corta o texto para
// inventar a separação produto × variante. Essa separação é sempre decisão explícita
// do operador (ex.: "Brim Profissional" é o nome do produto, não "Brim" + "Profissional").
function analyzeText(rawText) {
  const widthFound = extractWidth(rawText);
  const text = widthFound ? widthFound.cleanText : rawText;
  const largura = widthFound ? widthFound.width : '';
  const norm = normalize(text);
  let categorySlug = 'alfaiataria';
  for (const [slug, hints] of CATEGORY_HINTS) {
    if (hints.some((h) => norm.includes(h))) {
      categorySlug = slug;
      break;
    }
  }
  return {
    tecido: normalizeName(text),
    variante: '',
    categorySlug,
    largura,
    full: normalizeName(text),
  };
}

function findExactProduct(name) {
  const norm = normalize(name);
  if (!norm) return null;
  return getAllProducts().find((p) => normalize(p.name) === norm) || null;
}

// Nomes parecidos: contenção de tokens em qualquer direção (ex.: "Brim" ⊂ "Brim Misto"),
// excluindo nome idêntico. Usado apenas para AVISAR o operador — nunca para vincular
// automaticamente como variante de outro produto.
function findSimilarProducts(name) {
  const norm = normalize(name);
  const tokens = norm.split(/\s+/).filter((t) => t.length > 2);
  if (!tokens.length) return [];
  return getAllProducts().filter((p) => {
    const pn = normalize(p.name);
    if (pn === norm) return false;
    const ptoks = pn.split(/\s+/).filter((t) => t.length > 2);
    if (!ptoks.length) return false;
    return ptoks.every((t) => tokens.includes(t)) || tokens.every((t) => ptoks.includes(t));
  });
}

function findSimilarVariant(variantName, variants) {
  const tokens = normalize(variantName).split(/\s+/).filter((t) => t.length > 2);
  if (!tokens.length) return null;
  return variants.find((v) => tokens.every((t) => normalize(v.name).includes(t))) || null;
}

function generateDescription({ tecido, variante, categorySlug, observations, largura, composicao }) {
  const cat = getCategory(categorySlug);
  let parts = [
    variante?.trim()
      ? `${tecido} na variante ${variante}, uma opção para projetos de ${(cat?.name || '').toLowerCase()}.`
      : `${tecido}, uma opção para projetos de ${(cat?.name || '').toLowerCase()}.`,
  ];
  const obs = (observations || '')
    .split(/[,;.]+/)
    .map((o) => o.trim())
    .filter(Boolean);
  if (obs.length) {
    const last = obs[obs.length - 1];
    const list = obs.length > 1 ? `${obs.slice(0, -1).join(', ')} e ${last}` : last;
    parts.push(`Características informadas: ${list}.`);
  }
  if (composicao?.trim()) parts.push(`Composição: ${composicao.trim()}.`);
  if (largura?.trim()) parts.push(`Largura: ${largura.trim()}.`);
  return parts.join(' ');
}

const EMPTY_TECH = {
  largura: '',
  composicao: '',
  gramatura: '',
  elasticidade: 'Não informado',
  unidade: 'Metro',
  fabricante: '',
  observacoesTecnicas: '',
};

const ELASTICIDADE_OPTS = ['Não informado', 'Não', 'Baixa', 'Média', 'Alta'];
const UNIDADE_OPTS = ['Metro', 'Unidade'];

const inputCls =
  'mt-1 h-11 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15 disabled:bg-muted disabled:text-muted-foreground';
const labelCls = 'text-xs font-semibold uppercase tracking-wide text-muted-foreground';
const AI_HINT = 'Valor sugerido pela IA — confirme na etiqueta ou ficha técnica do fornecedor.';

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

export default function AdminNewProduct() {
  const navigate = useNavigate();
  const { user } = useQueue();
  const [step, setStep] = useState('modo');
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [tech, setTech] = useState(EMPTY_TECH);
  const [techSource, setTechSource] = useState('editar');
  const [aiObservations, setAiObservations] = useState('');
  const [description, setDescription] = useState('');
  const [photoChoice, setPhotoChoice] = useState('original');
  // Tipo do produto: com variantes (cada cor/estampa é uma variante real) ou sem variantes
  // (o próprio produto é a unidade comercial — nenhuma variante fictícia é criada).
  const [productKind, setProductKind] = useState('variantes');
  // Categoria pré-selecionada via /admin/produtos/novo?categoria=slug (botão ADICIONAR
  // PRODUTO da tela de categorias). O admin pode alterar antes de salvar.
  const [searchParams] = useSearchParams();
  const preCategory = searchParams.get('categoria');
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState('');
  // Sugestões da IA (ficha técnica): marcam os campos preenchidos pela análise para
  // exibir "Valor sugerido pela IA — confirme...". Edição manual limpa a marca.
  const [aiSuggested, setAiSuggested] = useState({});
  const [aiCategoryNotice, setAiCategoryNotice] = useState('');
  const [aiObsFromAI, setAiObsFromAI] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [aiError, setAiError] = useState('');
  const [descLoading, setDescLoading] = useState(false);

  // Destino explícito: attachSlug só é preenchido por escolha do operador (modo
  // "Adicionar variante" ou opção no aviso de nome parecido) — nunca pela IA.
  const [attachSlug, setAttachSlug] = useState('');
  const [conflictPicked, setConflictPicked] = useState(false);
  const attachProduct = useMemo(
    () => (attachSlug ? getAllProducts().find((p) => p.slug === attachSlug) || null : null),
    [attachSlug]
  );
  const exactProduct = useMemo(
    () => (suggestion ? findExactProduct(suggestion.tecido) : null),
    [suggestion]
  );
  const similarProducts = useMemo(
    () => (suggestion && !exactProduct ? findSimilarProducts(suggestion.tecido) : []),
    [suggestion, exactProduct]
  );

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    // Otimiza no navegador (preserva proporção, cor e estampa); na publicação o arquivo
    // sobe ao armazenamento durável do servidor e o banco guarda apenas a URL estável.
    if (file) setPhoto(await fileToDataUrl(file, 1200));
  };

  const setT = (key) => (e) => {
    setTech((t) => ({ ...t, [key]: e.target.value }));
    setAiSuggested((m) => (m[key] ? { ...m, [key]: false } : m));
  };

  const analyze = async () => {
    // Modo "Adicionar variante": o produto já foi escolhido explicitamente e o texto
    // digitado é SOMENTE a cor/estampa — nenhuma análise de nome é feita.
    if (productKind === 'adicionar') {
      if (!attachProduct) return;
      setSuggestion({
        tecido: attachProduct.name,
        variante: normalizeName(text),
        categorySlug: attachProduct.categorySlug,
        subcategorySlug: attachProduct.subcategorySlug,
        largura: '',
        full: attachProduct.name,
      });
      setTechSource('tecido');
      setTech({ ...EMPTY_TECH, largura: attachProduct.width || '', composicao: attachProduct.composition || '' });
      setDescription('');
      setDupeResolved(false);
      setConflictPicked(true);
      setStep('dados');
      return;
    }
    const s = analyzeText(text);
    // Categoria pré-selecionada (tela de categorias) tem prioridade sobre o palpite da IA.
    if (preCategory && getAllCategories().some((c) => c.slug === preCategory)) s.categorySlug = preCategory;
    setAttachSlug('');
    setConflictPicked(false);
    setTechSource('editar');
    setTech({ ...EMPTY_TECH, largura: s.largura || '' });
    setDescription('');
    setDupeResolved(false);
    setAiSuggested({});
    setAiCategoryNotice('');
    setAiObsFromAI(false);
    setAiObservations('');
    setAiError('');
    setAnalyzing(true);
    // Ficha técnica sugerida pela IA (backend + Gemini): preenche os campos da etapa
    // Dados como SUGESTÃO editável — NADA é salvo neste clique. Se a IA falhar, o fluxo
    // segue normalmente com os campos da heurística local.
    let ficha = null;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/analyze-fabric`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Role': user?.role || '' },
        body: JSON.stringify({ name: s.full }),
      });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail || ''; } catch { /* corpo não-JSON */ }
        const err = new Error(detail || `IA ${res.status}`);
        err.detail = detail;
        throw err;
      }
      ficha = await res.json();
    } catch (e) {
      setAiError(e.detail ? `${e.detail} A ficha segue editável manualmente.` : 'Não foi possível obter as sugestões da IA agora — preencha a ficha manualmente.');
    }
    setAnalyzing(false);
    if (ficha) {
      // Categoria: só aplica quando a IA casou com uma categoria EXISTENTE (e o admin não
      // pré-selecionou outra). Se a sugerida não existe, apenas avisa — nunca cria sozinho.
      if (!preCategory && ficha.categorySlug && getAllCategories().some((c) => c.slug === ficha.categorySlug)) {
        s.categorySlug = ficha.categorySlug;
        if (ficha.subcategorySlug) s.subcategorySlug = ficha.subcategorySlug;
      } else if (!preCategory) {
        setAiCategoryNotice(ficha.suggestedCategory || '');
      }
      const elastiOk = ELASTICIDADE_OPTS.includes(ficha.elasticity) && ficha.elasticity !== 'Não informado';
      setTech({
        ...EMPTY_TECH,
        largura: s.largura || ficha.width || '',
        composicao: ficha.composition || '',
        gramatura: ficha.gramatura || '',
        elasticidade: elastiOk ? ficha.elasticity : 'Não informado',
        unidade: ficha.unit === 'Unidade' ? 'Unidade' : 'Metro',
      });
      setAiSuggested({
        largura: !s.largura && !!ficha.width,
        composicao: !!ficha.composition,
        gramatura: !!ficha.gramatura,
        elasticidade: elastiOk,
        unidade: !!ficha.unit,
      });
      const notas = [
        ficha.characteristics?.length ? `Características sugeridas: ${ficha.characteristics.join(', ')}` : '',
        ficha.applications?.length ? `Aplicações sugeridas: ${ficha.applications.join(', ')}` : '',
      ].filter(Boolean).join('. ');
      if (notas) {
        setAiObservations(notas);
        setAiObsFromAI(true);
      }
    }
    setSuggestion(s);
    setStep('dados');
  };

  // Escolha explícita no aviso de nome parecido: vincular a um produto existente.
  // A variante é pré-preenchida com o trecho do nome que sobra (editável).
  const chooseAttach = (product) => {
    const ptoks = normalize(product.name).split(/\s+/);
    const rest = normalize(suggestion.full).split(/\s+/).filter((t) => !ptoks.includes(t));
    setAttachSlug(product.slug);
    setConflictPicked(true);
    setSuggestion({
      ...suggestion,
      tecido: product.name,
      variante: normalizeName(rest.join(' ')),
      categorySlug: product.categorySlug,
      subcategorySlug: product.subcategorySlug,
    });
    setTechSource('tecido');
    setTech({ ...EMPTY_TECH, largura: product.width || '', composicao: product.composition || '' });
  };

  // Descrição gerada a partir da ficha REVISADA (campos atuais da tela) — se a IA
  // falhar, cai para o gerador local. Nada é salvo aqui; a descrição é editável.
  const generateDesc = async () => {
    setDescLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/descricao-comercial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Role': user?.role || '' },
        body: JSON.stringify({
          name: suggestion.tecido,
          category: getCategory(suggestion.categorySlug)?.name || '',
          composition: tech.composicao,
          width: tech.largura,
          gramatura: tech.gramatura,
          elasticity: tech.elasticidade === 'Não informado' ? '' : tech.elasticidade,
          notes: aiObservations,
        }),
      });
      if (!res.ok) throw new Error(`IA ${res.status}`);
      const data = await res.json();
      if (!data.description) throw new Error('vazia');
      setDescription(data.description);
    } catch (e) {
      setDescription(generateDescription({ ...suggestion, variante: productKind === 'variantes' || productKind === 'adicionar' ? suggestion.variante : '', observations: aiObservations, largura: tech.largura, composicao: tech.composicao }));
    } finally {
      setDescLoading(false);
    }
  };

  const [dupeResolved, setDupeResolved] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState(null);

  const uniqueProductSlug = (name) => {
    const base = slugify(name) || `tecido-${Date.now()}`;
    const all = getAllProducts();
    let slug = base;
    let n = 2;
    while (all.some((p) => p.slug === slug)) {
      slug = `${base}-${n}`;
      n += 1;
    }
    return slug;
  };

  const uniqueVariantId = (product, name) => {
    const base = slugify(name) || `v${Date.now()}`;
    let id = base;
    let n = 2;
    while (product.variants.some((v) => v.id === id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    return id;
  };

  // Capa comercial (IA) gerada na etapa de revisão — opcional; só entra no produto após aprovação.
  const [coverAI, setCoverAI] = useState(null);
  // Classificações NOVAS digitadas na etapa Dados: vivem só no estado até a publicação final
  // (etapa 3 — Revisão). null = não está criando; string = nome digitado (pode estar vazio).
  // Cancelar ou voltar sem publicar não deixa categoria/subcategoria órfã cadastrada.
  const [pendingCategory, setPendingCategory] = useState(null);
  const [pendingSub, setPendingSub] = useState(null);
  const pendingCategoryDup =
    pendingCategory !== null && !!pendingCategory.trim() && getAllCategories().some((c) => c.slug === slugify(pendingCategory.trim()));
  const pendingSubDup =
    pendingSub !== null && !!pendingSub.trim() && getCustomSubcategories().some((s) => s.slug === slugify(pendingSub.trim()));
  const pendingInvalid =
    (pendingCategory !== null && !pendingCategory.trim()) ||
    (pendingSub !== null && !pendingSub.trim()) ||
    pendingCategoryDup ||
    pendingSubDup;

  // Vínculo com produto existente SÓ existe por escolha explícita do operador
  // (modo "Adicionar variante" ou opção no aviso de nome parecido) — nunca automático.
  const matchedActive = !!attachProduct && (productKind === 'variantes' || productKind === 'adicionar');
  const similarVariant = matchedActive && suggestion ? findSimilarVariant(suggestion.variante, attachProduct.variants) : null;
  const techReadonly = matchedActive && techSource === 'tecido';
  // Nome igual ou parecido com produto existente exige escolha explícita antes de avançar.
  const nameConflict = productKind === 'variantes' && !matchedActive && !!(exactProduct || similarProducts.length > 0);
  const conflictBlock = nameConflict && !conflictPicked;
  const variantMissing = !!suggestion && productKind === 'adicionar' && !suggestion.variante.trim();

  // APROVAR E PUBLICAR = confirmação humana final: grava no servidor (MongoDB via API).
  const publish = async () => {
    if (pendingInvalid || publishing || conflictBlock) return;
    if (!suggestion.tecido.trim()) {
      setPublishError('Informe o nome do tecido.');
      return;
    }
    if (productKind === 'adicionar' && !suggestion.variante.trim()) {
      setPublishError('Informe a Cor / Estampa / Variante.');
      return;
    }
    if (!matchedActive && exactProduct) {
      setPublishError(
        `Já existe o produto “${exactProduct.name}”. Para cadastrar cores/estampas nele, use a opção “Adicionar variante” na tela inicial — ou altere o nome do produto.`
      );
      return;
    }
    setPublishError('');
    setPublishing(true);
    try {
      // Classificações novas (digitadas na etapa Dados) só são efetivadas AQUI, na publicação.
      // Se o operador corrigiu a categoria (nova ou outra existente), a classificação escolhida
      // prevalece: cria-se produto NOVO em vez de adicionar variante ao produto existente.
      const useMatched = matchedActive;
      let finalCategorySlug = suggestion.categorySlug;
      let finalSubSlug = getSubcategories(suggestion.categorySlug).some((s) => s.slug === suggestion.subcategorySlug) ? suggestion.subcategorySlug : undefined;
      if (!useMatched && pendingCategory !== null) {
        finalCategorySlug = slugify(pendingCategory.trim());
        await addCustomCategory({ slug: finalCategorySlug, name: pendingCategory.trim(), tagline: '', description: '', custom: true });
        finalSubSlug = undefined;
      }
      if (!useMatched && pendingSub !== null) {
        finalSubSlug = slugify(pendingSub.trim());
        await addSubcategory({ slug: finalSubSlug, categorySlug: finalCategorySlug, name: pendingSub.trim() });
      }
      // A foto real sobe para o armazenamento durável; a imagem da categoria NUNCA
      // preenche foto de produto/variante — sem foto, fica FOTO_PENDENTE com placeholder.
      const image = photo ? (photo.startsWith('data:') ? await uploadImage(photo, `produtos/${slugify(suggestion.tecido)}`) : photo) : undefined;
      let slug;
      if (useMatched) {
        const baseVariant = {
          name: suggestion.variante,
          image,
          description,
          status: 'DISPONIVEL',
          statusHistory: [],
          photoStatus: image ? PHOTO_STATUS.APROVADA : PHOTO_STATUS.PENDENTE,
        };
        await addVariantToFabric(attachProduct.slug, { ...baseVariant, id: uniqueVariantId(attachProduct, suggestion.variante) });
        if (coverAI) await updateCustomProduct(attachProduct.slug, { coverImage: coverAI });
        slug = attachProduct.slug;
      } else if (productKind === 'sem') {
        // Produto SEM variantes: o próprio produto é a unidade comercial — variants: []
        // (nunca uma variante fictícia “Padrão”/“Opção”).
        slug = uniqueProductSlug(suggestion.tecido);
        await addCustomProduct({
          slug,
          name: suggestion.tecido.trim(),
          categorySlug: finalCategorySlug,
          subcategorySlug: finalSubSlug,
          hasVariants: false,
          coverImage: coverAI || undefined,
          image: photo || undefined,
          status: 'DISPONIVEL',
          description,
          composition: tech.composicao || 'Informação não preenchida',
          width: tech.largura || 'Informação não preenchida',
          colorTags: [],
          usageTags: [],
          keywords: [suggestion.tecido].filter(Boolean),
          featured: false,
          variants: [],
        });
      } else if (productKind === 'mae' || (productKind === 'variantes' && !suggestion.variante.trim())) {
        // Produto MÃE (ou "com variantes" sem variante informada): nasce sem nenhuma variante
        // (fica oculto no público até a primeira variante chegar) — as variantes entram depois
        // pelo modo "Adicionar variante" ou pelo cadastro em lote.
        slug = uniqueProductSlug(suggestion.tecido);
        await addCustomProduct({
          slug,
          name: suggestion.tecido.trim(),
          categorySlug: finalCategorySlug,
          subcategorySlug: finalSubSlug,
          hasVariants: true,
          status: 'DISPONIVEL',
          description,
          composition: tech.composicao || 'Informação não preenchida',
          width: tech.largura || 'Informação não preenchida',
          colorTags: [],
          usageTags: [],
          keywords: [suggestion.tecido].filter(Boolean),
          featured: false,
          variants: [],
        });
      } else {
        slug = uniqueProductSlug(suggestion.tecido);
        await addCustomProduct({
          slug,
          name: suggestion.tecido,
          categorySlug: finalCategorySlug,
          subcategorySlug: finalSubSlug,
          hasVariants: true,
          coverImage: coverAI || undefined,
          description,
          composition: tech.composicao || 'Informação não preenchida',
          width: tech.largura || 'Informação não preenchida',
          colorTags: [],
          usageTags: [],
          keywords: [suggestion.tecido, suggestion.variante].filter(Boolean),
          featured: false,
          variants: [
            {
              id: slugify(suggestion.variante) || 'v1',
              name: suggestion.variante,
              image,
              description,
              status: 'DISPONIVEL',
              statusHistory: [],
              photoStatus: image ? PHOTO_STATUS.APROVADA : PHOTO_STATUS.PENDENTE,
            },
          ],
        });
      }
      setPublishedSlug(slug);
      setStep('publicado');
    } catch (e) {
      setPublishError(e.message || 'Não foi possível publicar. Verifique sua conexão e tente novamente.');
    } finally {
      setPublishing(false);
    }
  };

  // Regra: alerta de possível duplicidade bloqueia a publicação até decisão explícita.
  const dupeBlock = !!(matchedActive && similarVariant && !dupeResolved);

  if (step === 'publicado') {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="admin-product-published">
        <ShieldCheck className="mx-auto h-12 w-12 text-emerald-600" strokeWidth={1.5} />
        <p className="mt-4 font-serif text-2xl text-navy">Cadastro aprovado e publicado</p>
        <p className="mt-2 text-sm text-muted-foreground">
          {matchedActive
            ? `Variante “${suggestion.variante}” adicionada ao tecido ${attachProduct.name} (${getCategory(suggestion.categorySlug)?.name}).`
            : productKind === 'sem'
              ? `Novo produto “${suggestion.tecido}” sem variantes — o próprio produto é a unidade comercial.`
              : productKind === 'mae' || (productKind === 'variantes' && !suggestion.variante.trim())
                ? `Produto “${suggestion.tecido}” criado — sem variantes ainda. Adicione as cores/estampas depois pelo modo “Adicionar variante” ou pelo cadastro em lote.`
                : `Novo tecido “${suggestion.tecido}” com a variante “${suggestion.variante}”.`}{' '}
          Cadastro gravado no servidor (banco de dados) e já visível no catálogo em qualquer dispositivo.
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {publishedSlug && (
            <Link
              to={`/produto/${publishedSlug}`}
              className="rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white hover:bg-navy-hover"
              data-testid="published-view-product"
            >
              VER NO CATÁLOGO
            </Link>
          )}
          <button
            onClick={() => navigate('/admin/produtos')}
            className="rounded-full border border-navy/20 px-7 py-3 text-sm font-semibold text-navy hover:bg-navy-light"
            data-testid="admin-product-published-back"
          >
            VOLTAR AOS PRODUTOS
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-new-product-page">
      <Link to="/admin/produtos" className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline" data-testid="admin-new-product-back">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>
      <h2 className="mt-3 font-serif text-2xl text-navy">Novo Produto</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Fluxo assistido: <strong>foto + texto livre</strong>. A IA propõe o cadastro — nome, categoria e
        ficha técnica sugerida — e você revisa tudo antes de aprovar. <strong>Nada é publicado automaticamente.</strong>
      </p>

      <ol className="mt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" data-testid="wizard-steps">
        {['Entrada', 'Dados', 'Revisão'].map((label, i) => {
          const keys = ['entrada', 'dados', 'revisao'];
          const active = keys.indexOf(step) >= i;
          return (
            <li key={label} className="flex items-center gap-2">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full ${active ? 'bg-navy text-white' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
              <span className={active ? 'text-navy' : 'text-muted-foreground'}>{label}</span>
              {i < 2 && <span className="text-muted-foreground">→</span>}
            </li>
          );
        })}
      </ol>

      <div className="mt-5 rounded-2xl border border-navy/10 bg-white p-6">
        {!(step === 'entrada' && productKind === 'mae') && (
          <>
        <p className="text-sm font-semibold text-navy">
          1. Foto {productKind === 'sem' ? 'do produto' : 'da variante'}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {productKind === 'sem'
            ? 'Foto do produto (vendido por metro, sem cores/estampas separadas).'
            : 'Cada variante visual (cor lisa, estampa, xadrez, poá, espinha de peixe...) tem sua própria fotografia.'}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label
            htmlFor="product-photo"
            data-testid="admin-photo-upload-label"
            className="flex h-36 w-36 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-navy/25 bg-navy-light/50 text-navy hover:border-navy"
          >
            {photo ? (
              <img src={photo} alt="Foto enviada da variante" className="h-full w-full object-cover" />
            ) : (
              <span className="flex flex-col items-center gap-1 px-2 text-center text-xs">
                <Camera className="h-6 w-6" /> Tirar / enviar foto
              </span>
            )}
          </label>
          <input id="product-photo" data-testid="admin-photo-upload-input" type="file" accept="image/*" capture="environment" className="sr-only" onChange={onPhoto} />
          <input id="product-photo-gallery" data-testid="admin-photo-gallery-input" type="file" accept="image/*" className="sr-only" onChange={onPhoto} />
          {!photo && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => document.getElementById('product-photo')?.click()}
                data-testid="admin-photo-camera-button"
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-navy text-xs font-bold text-white hover:bg-navy-hover"
              >
                <Camera className="h-4 w-4" /> TIRAR FOTO
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('product-photo-gallery')?.click()}
                data-testid="admin-photo-gallery-button"
                className="flex h-11 items-center justify-center gap-2 rounded-xl border border-navy/25 text-xs font-bold text-navy hover:bg-navy-light"
              >
                ESCOLHER DA GALERIA
              </button>
            </div>
          )}
        </div>
          </>
        )}

        {step === 'modo' && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="admin-new-product-mode">
            <button
              type="button"
              onClick={() => { setProductKind('variantes'); setStep('entrada'); }}
              data-testid="mode-individual"
              className="rounded-2xl border-2 border-navy/15 bg-white p-6 text-left transition-colors hover:border-navy"
            >
              <p className="font-serif text-lg text-navy">Produto novo (com variantes)</p>
              <p className="mt-1 text-sm text-muted-foreground">O nome digitado é preservado por inteiro — nunca vira variante de outro produto. Depois você informa a primeira cor/estampa.</p>
              <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-xs font-bold text-white">
                COMEÇAR <ArrowRight className="h-4 w-4" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/produtos/lote')}
              data-testid="mode-batch"
              className="rounded-2xl border-2 border-navy/15 bg-white p-6 text-left transition-colors hover:border-navy"
            >
              <p className="font-serif text-lg text-navy">Cadastro em lote por foto</p>
              <p className="mt-1 text-sm text-muted-foreground">Uma foto do conjunto gera várias variantes do mesmo tecido.</p>
              <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-navy px-5 py-3 text-xs font-bold text-navy">
                COMEÇAR <ArrowRight className="h-4 w-4" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => { setProductKind('sem'); setStep('entrada'); }}
              data-testid="mode-sem-variantes"
              className="rounded-2xl border-2 border-navy/15 bg-white p-6 text-left transition-colors hover:border-navy"
            >
              <p className="font-serif text-lg text-navy">Produto sem variantes</p>
              <p className="mt-1 text-sm text-muted-foreground">O próprio produto é a unidade comercial (vendido por metro) — o cliente só escolhe a metragem.</p>
              <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-navy px-5 py-3 text-xs font-bold text-navy">
                COMEÇAR <ArrowRight className="h-4 w-4" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => { setProductKind('mae'); setStep('entrada'); }}
              data-testid="mode-produto-mae"
              className="rounded-2xl border-2 border-navy/15 bg-white p-6 text-left transition-colors hover:border-navy"
            >
              <p className="font-serif text-lg text-navy">Produto mãe</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Cadastre só o tecido agora; as variantes entram depois (individual ou em lote).{' '}
                <strong className="text-foreground/80">Sem nenhuma variante cadastrada, o produto não aparece no site.</strong>
              </p>
              <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-navy px-5 py-3 text-xs font-bold text-navy">
                COMEÇAR <ArrowRight className="h-4 w-4" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => { setProductKind('adicionar'); setStep('entrada'); }}
              data-testid="mode-add-variant"
              className="rounded-2xl border-2 border-navy/15 bg-white p-6 text-left transition-colors hover:border-navy sm:col-span-2"
            >
              <p className="font-serif text-lg text-navy">Adicionar variante a produto existente</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Escolha primeiro o produto já cadastrado e informe apenas a nova cor/estampa — o nome do produto nunca é alterado.
              </p>
              <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-navy px-5 py-3 text-xs font-bold text-navy">
                COMEÇAR <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          </div>
        )}

        {step === 'entrada' && (
          <div className="mt-7">
            <p className="text-sm font-semibold text-navy">{productKind === 'mae' ? '1' : '2'}. Tipo de produto</p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="product-kind-choice">
              <button
                type="button"
                onClick={() => { setProductKind('variantes'); setAttachSlug(''); setConflictPicked(false); }}
                aria-pressed={productKind === 'variantes'}
                data-testid="kind-com-variantes"
                className={`rounded-xl border-2 px-4 py-3.5 text-left transition-colors ${
                  productKind === 'variantes' ? 'border-navy bg-navy-light/60 shadow-sm' : 'border-navy/15 hover:border-navy/40'
                }`}
              >
                <p className="text-sm font-bold text-navy">Com variantes</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Cada cor, estampa ou opção é uma variante real.</p>
              </button>
              <button
                type="button"
                onClick={() => { setProductKind('sem'); setAttachSlug(''); setConflictPicked(false); }}
                aria-pressed={productKind === 'sem'}
                data-testid="kind-sem-variantes"
                className={`rounded-xl border-2 px-4 py-3.5 text-left transition-colors ${
                  productKind === 'sem' ? 'border-navy bg-navy-light/60 shadow-sm' : 'border-navy/15 hover:border-navy/40'
                }`}
              >
                <p className="text-sm font-bold text-navy">Sem variantes</p>
                <p className="mt-0.5 text-xs text-muted-foreground">O próprio produto é a unidade comercial (vendido por metro).</p>
              </button>
              <button
                type="button"
                onClick={() => { setProductKind('mae'); setAttachSlug(''); setConflictPicked(false); }}
                aria-pressed={productKind === 'mae'}
                data-testid="kind-produto-mae"
                className={`rounded-xl border-2 px-4 py-3.5 text-left transition-colors ${
                  productKind === 'mae' ? 'border-navy bg-navy-light/60 shadow-sm' : 'border-navy/15 hover:border-navy/40'
                }`}
              >
                <p className="text-sm font-bold text-navy">Produto mãe</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Só o tecido agora; as variantes entram depois, pelo cadastro individual ou em lote.</p>
              </button>
              <button
                type="button"
                onClick={() => { setProductKind('adicionar'); setConflictPicked(false); }}
                aria-pressed={productKind === 'adicionar'}
                data-testid="kind-add-variant"
                className={`rounded-xl border-2 px-4 py-3.5 text-left transition-colors ${
                  productKind === 'adicionar' ? 'border-navy bg-navy-light/60 shadow-sm' : 'border-navy/15 hover:border-navy/40'
                }`}
              >
                <p className="text-sm font-bold text-navy">Adicionar variante</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Escolha um produto existente e adicione uma nova cor/estampa a ele.</p>
              </button>
            </div>
            {productKind === 'adicionar' ? (
              <>
                <label htmlFor="np-attach-product" className="mt-6 block text-sm font-semibold text-navy">
                  2. Produto existente que receberá a variante
                </label>
                <select
                  id="np-attach-product"
                  data-testid="attach-product-select"
                  value={attachSlug}
                  onChange={(e) => setAttachSlug(e.target.value)}
                  className="mt-1.5 h-12 w-full rounded-lg border border-input bg-white px-4 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                >
                  <option value="">Selecione o produto</option>
                  {getAllProducts()
                    .filter((p) => p.hasVariants !== false)
                    .map((p) => (
                      <option key={p.slug} value={p.slug}>
                        {`${p.name} — ${getCategory(p.categorySlug)?.name || ''}`}
                      </option>
                    ))}
                </select>
                <label htmlFor="np-freetext" className="mt-5 block text-sm font-semibold text-navy">
                  3. Cor / Estampa / Variante
                </label>
                <input
                  id="np-freetext"
                  data-testid="admin-freetext-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder='Ex: "Azul Royal", "Floral Rosa", "Xadrez Azul"'
                  className="mt-1.5 h-12 w-full rounded-lg border border-input px-4 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Aqui você informa SOMENTE a variante — o nome do produto escolhido nunca é alterado.
                </p>
                <button
                  onClick={analyze}
                  disabled={!attachSlug || !text.trim()}
                  data-testid="vendor-analyze-button"
                  className="mt-3 flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                  CONTINUAR <ArrowRight className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <label htmlFor="np-freetext" className="mt-6 block text-sm font-semibold text-navy">
                  {productKind === 'mae' ? '2' : '3'}. Nome do produto
                </label>
                <input
                  id="np-freetext"
                  data-testid="admin-freetext-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={
                    productKind === 'sem'
                      ? 'Ex: "Algodão Cru 2,65 m", "Forro Bela largura 1,40", "Percal largura: 2,50 m"'
                      : productKind === 'mae'
                        ? 'Ex: "Linho Rústico 2,50 m", "Cambraia Premium" — o texto inteiro vira o nome do produto'
                        : 'Ex: "Brim Profissional", "Linho Rústico", "Tricoline Estampada" — o texto inteiro vira o nome do produto'
                  }
                  className="mt-1.5 h-12 w-full rounded-lg border border-input px-4 outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  O nome digitado é <strong>preservado por inteiro</strong> — nenhuma palavra vira variante sozinha.
                  Na próxima etapa você pode informar a primeira cor/estampa (opcional — dá para cadastrar as variantes depois).
                  A IA também padroniza os nomes (ex: "micro fibra" → "Microfibra") e reconhece a largura no texto
                  (ex: "2,65 m"), sempre com sua confirmação.
                </p>
                <button
                  onClick={analyze}
                  disabled={!text.trim() || analyzing}
                  data-testid="vendor-analyze-button"
                  className="mt-3 flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Wand2 className="h-4 w-4" /> {analyzing ? 'ANALISANDO…' : 'ANALISAR COM IA'}
                </button>
              </>
            )}
          </div>
        )}

        {step !== 'entrada' && suggestion && (
          <div className="mt-6 rounded-2xl border border-navy/15 bg-navy-light/60 p-5" data-testid="ai-suggestion-panel">
            <p className="flex items-center gap-2 text-sm font-semibold text-navy">
              <Sparkles className="h-4 w-4" /> Sugestão da IA — corrija se necessário
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <Field label="Categoria" htmlFor="ai-category">
                <select
                  id="ai-category"
                  data-testid="ai-category-select"
                  value={pendingCategory !== null ? '__nova__' : suggestion.categorySlug}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__nova__') setPendingCategory('');
                    else {
                      setPendingCategory(null);
                      setSuggestion({ ...suggestion, categorySlug: v });
                    }
                  }}
                  disabled={matchedActive}
                  className={inputCls}
                >
                  {getAllCategories().map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                  <option value="__nova__" data-testid="ai-category-new-option">+ Criar nova categoria</option>
                </select>
                {matchedActive && (
                  <p className="mt-1 text-[11px] text-muted-foreground" data-testid="ai-category-locked-note">
                    A variante herda a classificação do produto “{attachProduct.name}”.
                  </p>
                )}
                {!matchedActive && aiCategoryNotice && pendingCategory === null && (
                  <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid="ai-category-suggestion-note">
                    Categoria sugerida pela IA: <strong>{aiCategoryNotice}</strong> — categoria ainda não cadastrada. Escolha uma existente acima ou crie a nova depois.
                  </p>
                )}
                {pendingCategory !== null && (
                  <>
                    <input
                      value={pendingCategory}
                      onChange={(e) => setPendingCategory(e.target.value)}
                      placeholder="Ex.: Religiosos"
                      aria-label="Nome da nova categoria"
                      data-testid="ai-new-category-input"
                      className={inputCls}
                    />
                    {pendingCategoryDup && (
                      <p className="mt-1 text-[11px] font-medium text-red-700" data-testid="ai-new-category-dup">Já existe uma categoria com este nome.</p>
                    )}
                    <p className="mt-1 text-[11px] text-muted-foreground">A categoria só será criada ao publicar o produto na etapa 3 — Revisão.</p>
                  </>
                )}
              </Field>
              <Field label="Subcategoria (opcional)" htmlFor="ai-subcategory">
                <select
                  id="ai-subcategory"
                  data-testid="ai-subcategory-select"
                  value={pendingSub !== null ? '__nova__' : suggestion.subcategorySlug || ''}
                  onChange={(e) => {
                    if (e.target.value === '__nova__') setPendingSub('');
                    else {
                      setPendingSub(null);
                      setSuggestion({ ...suggestion, subcategorySlug: e.target.value });
                    }
                  }}
                  disabled={matchedActive}
                  className={inputCls}
                >
                  <option value="">Sem subcategoria</option>
                  {(pendingCategory !== null ? [] : getSubcategories(suggestion.categorySlug)).map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                  <option value="__nova__" data-testid="ai-subcategory-new-option">+ Criar nova subcategoria</option>
                </select>
                {pendingSub !== null && (
                  <>
                    <input
                      value={pendingSub}
                      onChange={(e) => setPendingSub(e.target.value)}
                      placeholder="Ex.: Litúrgicos"
                      aria-label="Nome da nova subcategoria"
                      data-testid="ai-new-subcategory-input"
                      className={inputCls}
                    />
                    {pendingSubDup && (
                      <p className="mt-1 text-[11px] font-medium text-red-700" data-testid="ai-new-subcategory-dup">Já existe uma subcategoria com este nome.</p>
                    )}
                    <p className="mt-1 text-[11px] text-muted-foreground">A subcategoria só será criada ao publicar o produto na etapa 3 — Revisão.</p>
                  </>
                )}
              </Field>
              <Field label="Tecido" htmlFor="ai-tecido">
                <input
                  id="ai-tecido"
                  data-testid="ai-tecido-input"
                  value={suggestion.tecido}
                  onChange={(e) => { setSuggestion({ ...suggestion, tecido: e.target.value, full: e.target.value }); setConflictPicked(false); setAttachSlug(''); }}
                  disabled={matchedActive}
                  className={inputCls}
                />
              </Field>
              {(productKind === 'variantes' || productKind === 'adicionar') && (
                <Field label="Cor / Estampa / Variante" htmlFor="ai-variante">
                  <input
                    id="ai-variante"
                    data-testid="ai-variant-input"
                    value={suggestion.variante}
                    onChange={(e) => setSuggestion({ ...suggestion, variante: e.target.value })}
                    className={inputCls}
                  />
                  {productKind === 'variantes' && (
                    <p className="mt-1 text-[11px] text-muted-foreground" data-testid="variant-optional-hint">
                      Opcional: deixe em branco para cadastrar as cores/estampas depois (o produto fica oculto no site até a primeira variante).
                    </p>
                  )}
                </Field>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <span
                className={`rounded-full px-3.5 py-1.5 font-semibold ${matchedActive ? 'bg-navy text-white' : 'bg-white text-navy ring-1 ring-navy/25'}`}
                data-testid="ai-existing-badge"
              >
                Vincular a produto existente: {matchedActive ? `Sim — ${attachProduct.name}` : 'Não'}
              </span>
              <span
                className={`rounded-full px-3.5 py-1.5 font-semibold ${similarVariant ? 'bg-amber-500 text-white' : 'bg-white text-navy ring-1 ring-navy/25'}`}
                data-testid="ai-dupe-badge"
              >
                Possível duplicidade: {similarVariant ? 'Sim' : 'Não'}
              </span>
            </div>

            {nameConflict && (
              <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="name-conflict-panel">
                <p className="text-sm font-semibold text-amber-900">
                  {exactProduct
                    ? `Já existe um produto com este nome: “${exactProduct.name}” (${getCategory(exactProduct.categorySlug)?.name}).`
                    : `Nome parecido com ${similarProducts.length > 1 ? 'produtos existentes' : 'produto existente'}: ${similarProducts.map((p) => `“${p.name}”`).join(', ')}.`}
                </p>
                <p className="mt-1 text-xs text-amber-900/80">
                  O sistema não decide isso sozinho. Escolha o que deseja fazer:
                </p>
                <div className="mt-3 space-y-2">
                  <label className="flex items-start gap-3 rounded-xl border border-navy/15 bg-white p-3 text-sm" data-testid="conflict-choice-new-label">
                    <input
                      type="radio"
                      name="name-conflict"
                      checked={conflictPicked && !attachSlug}
                      onChange={() => {
                        setAttachSlug('');
                        setConflictPicked(true);
                        if (attachProduct) {
                          setSuggestion({ ...suggestion, tecido: suggestion.full, variante: '' });
                          setTechSource('editar');
                          setTech({ ...EMPTY_TECH, largura: suggestion.largura || '' });
                        }
                      }}
                      className="mt-0.5 h-4 w-4 accent-navy"
                      data-testid="conflict-choice-new"
                    />
                    <span>
                      <strong>Criar o produto novo “{suggestion.tecido}”.</strong> O nome digitado é preservado
                      por inteiro — nada vira variante de outro produto.
                    </span>
                  </label>
                  {(exactProduct ? [exactProduct] : similarProducts).map((p) => (
                    <label key={p.slug} className="flex items-start gap-3 rounded-xl border border-navy/15 bg-white p-3 text-sm" data-testid={`conflict-choice-attach-label-${p.slug}`}>
                      <input
                        type="radio"
                        name="name-conflict"
                        checked={attachSlug === p.slug}
                        onChange={() => chooseAttach(p)}
                        className="mt-0.5 h-4 w-4 accent-navy"
                        data-testid={`conflict-choice-attach-${p.slug}`}
                      />
                      <span>
                        <strong>Adicionar variante a “{p.name}”.</strong> Nenhum produto novo será criado — a
                        cor/estampa será preenchida com o trecho restante do nome (editável).
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {(productKind === 'sem' || productKind === 'mae') && exactProduct && (
              <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-medium text-red-700" data-testid="exact-name-warning">
                Já existe um produto com este nome: “{exactProduct.name}”. Altere o nome ou cancele — a publicação será bloqueada.
              </p>
            )}

            {matchedActive && (
              <div className="mt-4" data-testid="dupe-check-found">
                <p className="text-xs font-semibold text-navy">
                  {similarVariant
                    ? `POSSÍVEL DUPLICIDADE ENCONTRADA — “${similarVariant.name}” já existe em ${attachProduct.name}.`
                    : `${attachProduct.name} já existe em ${getCategory(attachProduct.categorySlug)?.name}. “${suggestion.variante}” será adicionada como nova variante.`}
                </p>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="dupe-existing-variants">
                  {attachProduct.variants.map((v) => (
                    <span
                      key={v.id}
                      className={`flex items-center gap-1.5 rounded-full border bg-white py-1 pl-1 pr-3 text-xs ${similarVariant?.id === v.id ? 'border-amber-500 ring-1 ring-amber-400' : 'border-navy/15'}`}
                    >
                      <img src={v.image} alt={v.name} className="h-7 w-7 rounded-full object-cover" />
                      {v.name}
                      {similarVariant?.id === v.id && <strong className="text-amber-700">· semelhante</strong>}
                    </span>
                  ))}
                </div>
                {similarVariant && (
                  <Link to={`/produto/${attachProduct.slug}`} target="_blank" className="mt-2 inline-block text-xs font-semibold text-navy underline" data-testid="dupe-view-product-button">
                    Ver produto existente
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        {step === 'dados' && suggestion && (
          <div className="mt-7" data-testid="tech-data-section">
            <p className="text-sm font-semibold text-navy">3. Dados técnicos</p>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              Campos marcados foram <strong>preenchidos pela IA como sugestão típica de mercado</strong> —
              revise, edite ou apague: só vale o que você confirmar. Campos vazios serão marcados como
              “Informação não preenchida”.
            </p>
            {Object.values(aiSuggested).some(Boolean) && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-800" data-testid="ai-tech-suggestion-banner">
                Sugestão da IA — revise e altere se necessário.
              </p>
            )}
            {aiError && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-800" data-testid="ai-ficha-error">
                {aiError}
              </p>
            )}

            {matchedActive && (
              <div className="mt-4 rounded-xl border border-navy/15 bg-white p-4" data-testid="fabric-data-source">
                <p className="text-xs font-semibold text-navy">Tecido existente: {attachProduct.name}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Largura atual: {attachProduct.width || '—'} · Composição atual: {attachProduct.composition || '—'} · Unidade: Metro
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setTechSource('tecido');
                      setTech({ ...EMPTY_TECH, largura: attachProduct.width || '', composicao: attachProduct.composition || '' });
                    }}
                    data-testid="use-fabric-data-button"
                    aria-pressed={techSource === 'tecido'}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold ${techSource === 'tecido' ? 'border-navy bg-navy text-white' : 'border-navy/30 text-navy hover:bg-navy-light'}`}
                  >
                    USAR DADOS DO TECIDO
                  </button>
                  <button
                    onClick={() => setTechSource('editar')}
                    data-testid="edit-fabric-data-button"
                    aria-pressed={techSource === 'editar'}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold ${techSource === 'editar' ? 'border-navy bg-navy text-white' : 'border-navy/30 text-navy hover:bg-navy-light'}`}
                  >
                    EDITAR DADOS
                  </button>
                </div>
              </div>
            )}

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Largura" htmlFor="tech-width">
                <input id="tech-width" data-testid="tech-width-input" value={tech.largura} onChange={setT('largura')} placeholder="Ex: 1,50 m" disabled={techReadonly} className={inputCls} />
                {aiSuggested.largura && <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid="ai-suggested-largura">{AI_HINT}</p>}
              </Field>
              <Field label="Composição" htmlFor="tech-composition">
                <input id="tech-composition" data-testid="tech-composition-input" value={tech.composicao} onChange={setT('composicao')} placeholder="Ex: 70% poliéster / 30% viscose" disabled={techReadonly} className={inputCls} />
                {aiSuggested.composicao && <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid="ai-suggested-composicao">{AI_HINT}</p>}
              </Field>
              <Field label="Gramatura (opcional)" htmlFor="tech-gramatura">
                <input id="tech-gramatura" data-testid="tech-gramatura-input" value={tech.gramatura} onChange={setT('gramatura')} placeholder="Ex: 220 g/m²" disabled={techReadonly} className={inputCls} />
                {aiSuggested.gramatura && <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid="ai-suggested-gramatura">{AI_HINT}</p>}
              </Field>
              <Field label="Elasticidade" htmlFor="tech-elasticity">
                <select id="tech-elasticity" data-testid="tech-elasticity-select" value={tech.elasticidade} onChange={setT('elasticidade')} disabled={techReadonly} className={inputCls}>
                  {ELASTICIDADE_OPTS.map((o) => <option key={o}>{o}</option>)}
                </select>
                {aiSuggested.elasticidade && <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid="ai-suggested-elasticidade">{AI_HINT}</p>}
              </Field>
              <Field label="Unidade de venda" htmlFor="tech-unit">
                <select id="tech-unit" data-testid="tech-unit-select" value={tech.unidade} onChange={setT('unidade')} disabled={techReadonly} className={inputCls}>
                  {UNIDADE_OPTS.map((o) => <option key={o}>{o}</option>)}
                </select>
                {aiSuggested.unidade && <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid="ai-suggested-unidade">{AI_HINT}</p>}
              </Field>
              <Field label="Fabricante / Linha (opcional)" htmlFor="tech-fabricante">
                <input id="tech-fabricante" data-testid="tech-fabricante-input" value={tech.fabricante} onChange={setT('fabricante')} disabled={techReadonly} className={inputCls} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Observações técnicas (opcional)" htmlFor="tech-obs">
                  <textarea id="tech-obs" data-testid="tech-observacoes-input" value={tech.observacoesTecnicas} onChange={setT('observacoesTecnicas')} rows={2} disabled={techReadonly} className="mt-1 w-full rounded-lg border border-input bg-white px-3 py-2.5 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15 disabled:bg-muted" />
                </Field>
              </div>
            </div>

            <div className="mt-6 border-t border-navy/10 pt-5">
              <label htmlFor="ai-observations" className="text-sm font-semibold text-navy">
                4. Observações para a IA (características que você conhece)
              </label>              <textarea
                id="ai-observations"
                data-testid="ai-observations-input"
                value={aiObservations}
                onChange={(e) => { setAiObservations(e.target.value); setAiObsFromAI(false); }}
                rows={2}
                placeholder='Ex: "leve, fresco, não amassa fácil, bom para camisa e calça"'
                className="mt-1.5 w-full rounded-lg border border-input bg-white px-4 py-3 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
              />
              {aiObsFromAI && (
                <p className="mt-1 text-[11px] font-medium text-amber-700" data-testid="ai-observations-suggested">
                  Sugestão da IA — revise e altere se necessário (apague o que não se aplica).
                </p>
              )}
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                O que permanecer aqui poderá ser usado na descrição. A IA pode sugerir características —
                revise antes: nada entra na descrição sem passar pela sua revisão.
              </p>
              <button
                onClick={generateDesc}
                disabled={descLoading}
                data-testid="vendor-generate-description-button"
                className="mt-3 flex items-center gap-2 rounded-full border border-navy px-5 py-2.5 text-sm font-semibold text-navy hover:bg-navy-light disabled:cursor-not-allowed disabled:opacity-40"
              >
                <FileText className="h-4 w-4" /> {descLoading ? 'GERANDO…' : 'GERAR DESCRIÇÃO COM IA'}
              </button>
              {description && (
                <div className="mt-3 rounded-xl bg-white p-4 ring-1 ring-navy/15" data-testid="ai-description-preview">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Descrição sugerida</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground" data-testid="ai-description-text">{description}</p>
                  <p className="mt-2 text-xs text-muted-foreground">Você poderá editar na tela de revisão.</p>
                </div>
              )}
            </div>

            <button
              onClick={() => description && !pendingInvalid && !conflictBlock && !variantMissing && setStep('revisao')}
              disabled={!description || pendingInvalid || conflictBlock || variantMissing}
              data-testid="vendor-review-button"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold tracking-wide text-white hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              REVISAR CADASTRO <ArrowRight className="h-4 w-4" />
            </button>
            {!description && (
              <p className="mt-2 text-center text-xs text-muted-foreground">Gere a descrição para avançar à revisão.</p>
            )}
            {description && variantMissing && (
              <p className="mt-2 text-center text-xs font-medium text-amber-800" data-testid="variant-required-note">
                Informe a Cor / Estampa / Variante no quadro da sugestão da IA para continuar.
              </p>
            )}
            {description && !variantMissing && conflictBlock && (
              <p className="mt-2 text-center text-xs font-medium text-amber-800" data-testid="conflict-choice-required">
                Escolha no aviso acima: criar produto novo ou adicionar variante ao produto existente.
              </p>
            )}
            {description && pendingInvalid && (
              <p className="mt-2 text-center text-xs font-medium text-amber-800" data-testid="dados-pending-classification-error">
                Complete o nome da nova categoria/subcategoria (ou escolha uma existente) para continuar.
              </p>
            )}
          </div>
        )}

        {step === 'revisao' && suggestion && (
          <div className="mt-7" data-testid="review-panel">
            <p className="flex items-center gap-2 text-sm font-semibold text-navy">
              <Pencil className="h-4 w-4" /> 5. Revisão — todos os campos podem ser editados
            </p>

            <div className="mt-4 rounded-xl border-2 border-navy/25 bg-navy-light/60 p-4" data-testid="review-action-summary">
              <p className="text-sm font-bold text-navy">
                {matchedActive
                  ? `AÇÃO: adicionar a variante “${suggestion.variante}” ao produto existente “${attachProduct.name}” — nenhum produto novo será criado.`
                  : productKind === 'sem'
                    ? `AÇÃO: criar o produto novo “${suggestion.tecido}” (sem variantes — vendido por metro).`
                    : productKind === 'mae'
                      ? `AÇÃO: criar o produto mãe “${suggestion.tecido}” — as variantes serão adicionadas depois.`
                      : suggestion.variante.trim()
                        ? `AÇÃO: criar o produto novo “${suggestion.tecido}” com a primeira variante “${suggestion.variante}”.`
                        : `AÇÃO: criar o produto novo “${suggestion.tecido}” — sem variantes agora; as cores/estampas serão adicionadas depois (fica oculto no site até a primeira variante).`}
              </p>
            </div>

            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Foto</p>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPhotoChoice('original')}
                  data-testid="photo-choice-original"
                  aria-pressed={photoChoice === 'original'}
                  className={`rounded-xl border-2 p-2 text-left ${photoChoice === 'original' ? 'border-navy shadow-md' : 'border-navy/10'}`}
                >
                  {photo ? (
                    <img src={photo} alt="Foto original" className="aspect-square w-full rounded-lg object-cover" data-testid="review-photo-original" />
                  ) : (
                    <div className="flex aspect-square items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground" data-testid="review-photo-original">sem foto</div>
                  )}
                  <span className="mt-1.5 block text-center text-xs font-semibold">ORIGINAL {photoChoice === 'original' && '✓'}</span>
                </button>
                <button
                  onClick={() => photo && setPhotoChoice('padronizada')}
                  data-testid="photo-choice-standardized"
                  aria-pressed={photoChoice === 'padronizada'}
                  className={`rounded-xl border-2 p-2 text-left ${photoChoice === 'padronizada' ? 'border-navy shadow-md' : 'border-navy/10'} ${!photo ? 'opacity-50' : ''}`}
                >
                  {photo ? (
                    <div className="aspect-square w-full rounded-lg bg-white p-3 ring-1 ring-navy/15">
                      <img src={photo} alt="Foto padronizada (simulação)" className="h-full w-full rounded-md object-cover" style={{ filter: 'brightness(1.06) contrast(1.03) saturate(1.02)' }} data-testid="review-photo-standardized" />
                    </div>
                  ) : (
                    <div className="flex aspect-square items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground" data-testid="review-photo-standardized">sem foto</div>
                  )}
                  <span className="mt-1.5 block text-center text-xs font-semibold">PADRONIZADA (simulação) {photoChoice === 'padronizada' && '✓'}</span>
                </button>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                A padronização ajusta enquadramento, proporção e iluminação — <strong>sem jamais
                alterar cor, tonalidade, estampa, desenho ou textura reais do tecido</strong>.
              </p>
            </div>

            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Identificação</p>
              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                <Field label="Categoria" htmlFor="rev-category">
                  <select id="rev-category" data-testid="review-category-select" value={pendingCategory !== null ? '__nova__' : suggestion.categorySlug} onChange={(e) => { if (e.target.value !== '__nova__') { setPendingCategory(null); setSuggestion({ ...suggestion, categorySlug: e.target.value }); } }} disabled={matchedActive} className={inputCls}>
                    {getAllCategories().map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    {pendingCategory !== null && <option value="__nova__">{(pendingCategory.trim() || 'Nova categoria') + ' (nova)'}</option>}
                  </select>
                </Field>
                <Field label="Subcategoria (opcional)" htmlFor="rev-subcategory">
                  <select id="rev-subcategory" data-testid="review-subcategory-select" value={pendingSub !== null ? '__nova__' : suggestion.subcategorySlug || ''} onChange={(e) => { if (e.target.value !== '__nova__') { setPendingSub(null); setSuggestion({ ...suggestion, subcategorySlug: e.target.value }); } }} disabled={matchedActive} className={inputCls}>
                    <option value="">Sem subcategoria</option>
                    {(pendingCategory !== null ? [] : getSubcategories(suggestion.categorySlug)).map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                    {pendingSub !== null && <option value="__nova__">{(pendingSub.trim() || 'Nova subcategoria') + ' (nova)'}</option>}
                  </select>
                </Field>
                <Field label="Tecido" htmlFor="rev-tecido">
                  <input id="rev-tecido" data-testid="review-tecido-input" value={suggestion.tecido} onChange={(e) => setSuggestion({ ...suggestion, tecido: e.target.value, full: e.target.value })} disabled={matchedActive} className={inputCls} />
                </Field>
                {(productKind === 'variantes' || productKind === 'adicionar') && (
                  <Field label="Cor / Estampa / Variante" htmlFor="rev-variante">
                    <input id="rev-variante" data-testid="review-variant-input" value={suggestion.variante} onChange={(e) => setSuggestion({ ...suggestion, variante: e.target.value })} className={inputCls} />
                  </Field>
                )}
              </div>
            </div>

            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Dados técnicos</p>
              <dl className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3" data-testid="review-tech-summary">
                {[
                  ['Largura', tech.largura],
                  ['Composição', tech.composicao],
                  ['Gramatura', tech.gramatura],
                  ['Elasticidade', tech.elasticidade === 'Não informado' ? '' : tech.elasticidade],
                  ['Unidade de venda', tech.unidade],
                  ['Fabricante/Linha', tech.fabricante],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-navy-light/60 p-3">
                    <dt className="text-[11px] text-muted-foreground">{label}</dt>
                    <dd className={`mt-0.5 font-medium ${value ? '' : 'italic text-muted-foreground'}`} data-testid={`review-tech-${normalize(label).replace(/\s+/g, '-')}`}>
                      {value || 'Informação não preenchida'}
                    </dd>
                  </div>
                ))}
              </dl>
              {tech.observacoesTecnicas && (
                <p className="mt-2 text-xs text-muted-foreground">Obs. técnicas: {tech.observacoesTecnicas}</p>
              )}
              <button onClick={() => setStep('dados')} className="mt-2 text-xs font-semibold text-navy underline" data-testid="review-edit-tech-button">
                Editar dados técnicos
              </button>
            </div>

            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Conteúdo gerado</p>
              <div className="mt-2 grid gap-3">
                <div className="rounded-lg bg-muted/70 p-3">
                  <p className="text-[11px] text-muted-foreground">Observações fornecidas</p>
                  <p className="mt-0.5 text-sm" data-testid="review-observations">{aiObservations || '—'}</p>
                </div>
                <div>
                  <label htmlFor="rev-description" className="text-[11px] font-semibold text-muted-foreground">Descrição sugerida pela IA (editável)</label>
                  <textarea
                    id="rev-description"
                    data-testid="review-description-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="mt-1 w-full rounded-lg border border-input bg-white px-4 py-3 text-sm leading-relaxed outline-none focus:border-navy focus:ring-2 focus:ring-navy/15"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Verificação</p>
              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                <span className={`rounded-full px-3.5 py-1.5 font-semibold ${matchedActive ? 'bg-navy text-white' : 'bg-white text-navy ring-1 ring-navy/25'}`} data-testid="review-existing-badge">
                  Produto existente: {matchedActive ? `Sim — ${attachProduct.name}` : 'Não'}
                </span>
                <span className={`rounded-full px-3.5 py-1.5 font-semibold ${similarVariant ? 'bg-amber-500 text-white' : 'bg-white text-navy ring-1 ring-navy/25'}`} data-testid="review-dupe-badge">
                  Possível duplicidade: {similarVariant ? `Sim — “${similarVariant.name}”` : 'Não'}
                </span>
              </div>
              {matchedActive && !similarVariant && (
                <p className="mt-2 text-xs text-navy" data-testid="review-new-variant-note">
                  Será criada uma <strong>nova variante</strong> do tecido existente — não um novo produto.
                </p>
              )}
              {!matchedActive && (
                <p className="mt-2 text-xs text-navy" data-testid="review-new-product-note">
                  Será criado um <strong>produto novo</strong> — nenhum produto existente será alterado.
                </p>
              )}
            </div>

            <div className="mt-5 border-t border-navy/10 pt-5" data-testid="review-cover-ai">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Foto principal (capa comercial, opcional)</p>
              <div className="mt-2">
                <CoverAIGenerator
                  productName={suggestion.tecido}
                  variants={[{ id: 'nova', name: suggestion.variante, image: photo || null }]}
                  cover={coverAI}
                  onApprove={setCoverAI}
                  onRemove={() => setCoverAI(null)}
                />
              </div>
            </div>

            {pendingInvalid && (
              <p className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800" data-testid="review-pending-classification-error">
                Complete o nome da nova categoria/subcategoria (ou escolha uma existente) antes de publicar.
              </p>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={() => setStep('dados')}
                className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy-light"
                data-testid="review-back-button"
              >
                <ArrowLeft className="h-4 w-4" /> VOLTAR
              </button>
              <div className="flex-1">
                {dupeBlock && (
                  <div className="mb-3 rounded-xl border border-amber-400 bg-amber-50 p-4" data-testid="dupe-block-notice">
                    <p className="text-sm font-semibold text-amber-800">
                      Publicação bloqueada: possível duplicidade com “{similarVariant.name}” em {attachProduct.name}.
                    </p>
                    <p className="mt-1 text-xs text-amber-800/80">
                      Confirme abaixo que NÃO é duplicado para liberar a publicação, ou volte e ajuste o cadastro.
                    </p>
                    <button
                      type="button"
                      onClick={() => setDupeResolved(true)}
                      data-testid="dupe-resolve-not-duplicate"
                      className="mt-3 rounded-full bg-amber-600 px-5 py-2.5 text-xs font-bold text-white hover:bg-amber-700"
                    >
                      NÃO É DUPLICADO — LIBERAR PUBLICAÇÃO
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={publish}
                  disabled={dupeBlock || publishing || conflictBlock}
                  data-testid="admin-confirm-publish-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold tracking-wide text-white hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ShieldCheck className="h-4 w-4" /> {publishing ? 'PUBLICANDO…' : 'APROVAR E PUBLICAR'}
                </button>
                {publishError && (
                  <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-center text-xs font-medium text-red-700" role="alert" data-testid="publish-error">
                    {publishError}
                  </p>
                )}
              </div>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              A IA é apenas assistente: a aprovação humana é obrigatória.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
